/**
 * This function returns state to the item above if it exists in the flat list
 * otherwise below or empty state if no other items exist
 */
export const getNextItemState = <T>(items: Array<{ id: string } & T>, focusedItemId: string | undefined): { id: string } & T => {
  let nextItem
  if (!focusedItemId) {
    nextItem = items[0]
  } else {
    const idx = items.findIndex(({ id }) => id === focusedItemId)
    nextItem = items[idx > 0 ? idx - 1 : idx + 1]
  }
  return nextItem
}

/**
 * This function returns state to the item above if it exists in the same nesting level otherwise below.
 * If no other items on the same nesting level exists, it returns parent state or empty state if parent item doesn't exist
 */
type NestedItem<T> = {
  id: string,
  children?: NestedItem<T>[]
} & T

export const getNextNestedItemState = <T>(items: NestedItem<T>[], focusedItemId?: string, parentArray?: NestedItem<T>[]): NestedItem<T> | null => {
  if (!focusedItemId) {
    return items[0]
  }

  for (const item of items) {

    if (item.id === focusedItemId) {
      const nextItem = getNextItemState(items, focusedItemId)
      if (!nextItem && parentArray?.length) {
        return parentArray.find(item => item.children?.some(childItem => childItem.id === focusedItemId)) ?? null
      }
      return nextItem
    }
    if (item.children && item.children.length > 0) {
      const replacement = getNextNestedItemState(item.children, focusedItemId, items)
      if (replacement) {
        return replacement
      }
    }
  }

  return null
}

/**
 * This function returns position to the item under the focused item if it exists 
 * otherwise 0 as initial position
 */
export const getNextItemPosition = <T>(items: Array<{ id: string } & T>, focusedItemId: string | undefined): number => focusedItemId ? (items.findIndex(({ id }) => id === focusedItemId) + 1) : 0

/**
 * This function loops through with incremental property names.
 * Returns the first found by the generator item.
 * Limits looping to the provided limit.
 */
export const generateUniqueItem = async <T>(generator: (increment: number) => Promise<T>, limit = 30, initialIncrement = 1): Promise<[T, number]> => {
  let result
  let increment = initialIncrement

  while (increment < limit && !result) {
    try {
      result = await generator(increment)
    } catch {
      result = undefined
    }
    if (!result) {
      increment += 1
    }
  }

  return [result, increment]
}

export const reorderList = <T>(array: T[], sourceIndex: number, targetIndex: number): T[] => {
  const newArray = [...array]
  const element = newArray.splice(sourceIndex, 1)[0]
  newArray.splice(targetIndex, 0, element)
  return newArray
}